import axios from 'axios';
import { buildApiUrl } from '../config/api';

const walletService = {
    getToken: () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.warn('⚠️ No token found in localStorage');
        }
        return token;
    },

    // Get wallet details
    getWallet: async (limit = 20, offset = 0) => {
        try {
            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    message: 'Please login to view wallet',
                    data: null
                };
            }

            const response = await axios.get(`${buildApiUrl('/wallet/my-wallet')}?limit=${limit}&offset=${offset}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Wallet fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Get wallet error:', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch wallet',
                data: null
            };
        }
    },

    // Get wallet balance
    getBalance: async () => {
        try {
            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    balance: 0,
                    message: 'Please login to view balance'
                };
            }

            const response = await axios.get(buildApiUrl('/wallet/balance'), {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Wallet balance:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Get balance error:', error.response?.data || error.message);
            return {
                success: false,
                balance: 0,
                message: error.response?.data?.message || 'Failed to fetch balance'
            };
        }
    },

    // Get transactions
    getTransactions: async (filters = {}) => {
        try {
            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    data: [],
                    total: 0,
                    message: 'Please login to view transactions'
                };
            }

            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.offset) params.append('offset', filters.offset);
            if (filters.from_date) params.append('from_date', filters.from_date);
            if (filters.to_date) params.append('to_date', filters.to_date);

            const response = await axios.get(`${buildApiUrl('/wallet/transactions')}?${params.toString()}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Transactions fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Get transactions error:', error.response?.data || error.message);
            return {
                success: false,
                data: [],
                total: 0,
                message: error.response?.data?.message || 'Failed to fetch transactions'
            };
        }
    },

    // Apply wallet payment
    applyPayment: async (amount, bookingId, bookingType) => {
        try {
            if (!amount || amount <= 0) {
                return {
                    success: false,
                    message: 'Invalid payment amount'
                };
            }

            if (!bookingId) {
                return {
                    success: false,
                    message: 'Booking ID is required'
                };
            }

            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    message: 'Please login to make payment'
                };
            }

            const response = await axios.post(buildApiUrl('/wallet/apply-payment'), {
                amount,
                bookingId,
                bookingType: bookingType || 'package'
            }, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Wallet payment applied:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Apply payment error:', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || 'Payment failed'
            };
        }
    },

    // Add reward to wallet (Admin only)
    addReward: async (userId, amount, description) => {
        try {
            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    message: 'Please login to add reward'
                };
            }

            const response = await axios.post(buildApiUrl('/wallet/add-reward'), {
                userId,
                amount,
                description
            }, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Reward added:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Add reward error:', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to add reward'
            };
        }
    },

    // Process refund to wallet (Admin only)
    processRefund: async (bookingId, amount, bookingType) => {
        try {
            const token = walletService.getToken();
            if (!token) {
                return {
                    success: false,
                    message: 'Please login to process refund'
                };
            }

            const response = await axios.post(buildApiUrl('/wallet/process-refund'), {
                bookingId,
                amount,
                bookingType: bookingType || 'package'
            }, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            console.log('✅ Refund processed:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Process refund error:', error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to process refund'
            };
        }
    }
};

export default walletService;
